import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../../../../core/services/api.service';

export interface OwnerPortalUnitGallery {
  public_id: string;
  secure_url: string;
  format?: string;
  _id?: string;
}

export interface OwnerPortalUnitBasePrice {
  _id?: string;
  price: number;
  currency: string;
  tiers?: { minDays: number; pricePerDay: number; label?: string; _id?: string }[];
}

export interface OwnerPortalUnitI18n {
  ar: string;
  en: string;
}

export interface OwnerPortalUnit {
  _id: string;
  title?: string;
  title_en?: string;
  title_ar?: string;
  description_en?: string;
  description_ar?: string;
  unitType?: string;
  unitType_en?: string;
  unitType_ar?: string;
  unitNumber?: string;
  slug?: string;
  buildYear?: number;
  status?: string;
  purpose?: string;
  ownerType?: string;
  ownerId?: string;
  unitArea?: number;
  bedrooms?: number;
  bathrooms?: number;
  floorNumber?: number;
  furnished?: boolean;
  adults?: number;
  children?: number;
  isFeatured?: boolean;
  isVerified?: boolean;
  isTrusted?: boolean;
  managedByHomiGo?: boolean;
  managementType?: string;
  approvedStatus?: string;
  totalReviews?: number;
  averageRating?: number;
  viewsCount?: number;
  gallery?: OwnerPortalUnitGallery[];
  facilitiesAndServices_en?: string[];
  facilitiesAndServices_ar?: string[];
  facilitiesAndServices?: string[];
  utilities_en?: string[];
  utilities_ar?: string[];
  basePrice?: OwnerPortalUnitBasePrice;
  location?: {
    country?: OwnerPortalUnitI18n | string;
    city?: OwnerPortalUnitI18n | string;
    area?: OwnerPortalUnitI18n | string;
    address?: OwnerPortalUnitI18n | string;
    coordinates?: {
      type: string;
      coordinates: [number, number];
    };
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface OwnerPortalOverviewResponse {
  // Stats (may come from a separate endpoint)
  unitsCount: number;
  bookingsCount: number;
  totalRevenue: number;
  // Units list (paginated)
  units: OwnerPortalUnit[];
  pages?: number;
  currentPage?: number;
  totalItems?: number;
}

export interface OwnerPortalProfile {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  lang?: string;
  ShowProfile?: string;
  aboutMe?: string;
  profileImage?: string;
  role?: string;
  location?: string;
  numberOfPropertiesOwned?: number;
  notificationSettings?: {
    email?: boolean;
    app?: boolean;
  };
}

export interface OwnerPortalProfileResponse {
  resProfile: OwnerPortalProfile;
}

export interface OwnerPortalTransaction {
  _id: string;
  ownerId?: string;
  type: string;
  amount: number;
  referenceId?: string;
  description?: string;
  createdAt: string;
}

export interface OwnerPortalPayout {
  _id: string;
  ownerId?: string;
  amount: number;
  status: string;
  requestedAt?: string;
  processedAt?: string;
  transactionId?: string;
}

export interface OwnerPortalWalletResponse {
  balance: number;
  totalRevenue: number;
  totalDeductions: number;
  withdrawn: number;
  transactions: OwnerPortalTransaction[];
  payouts: OwnerPortalPayout[];
}

export interface OwnerPortalProfileUpdatePayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  aboutMe?: string;
  location?: string;
  numberOfPropertiesOwned?: string;
  attachment?: File;
}

@Injectable({ providedIn: 'root' })
export class OwnerPortalDashboardService {
  private readonly overviewSubject = new BehaviorSubject<OwnerPortalOverviewResponse | null>(null);
  private readonly profileSubject = new BehaviorSubject<OwnerPortalProfile | null>(null);
  private readonly walletSubject = new BehaviorSubject<OwnerPortalWalletResponse | null>(null);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string>('');

  readonly overview$ = this.overviewSubject.asObservable();
  readonly profile$ = this.profileSubject.asObservable();
  readonly wallet$ = this.walletSubject.asObservable();
  readonly isLoading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  private isFetchingOverview = false;
  private isFetchingProfile = false;
  private isFetchingWallet = false;

  constructor(private apiService: ApiService) { }

  loadOverview(forceRefresh = false): void {
    if (this.isFetchingOverview) return;
    if (!forceRefresh && this.overviewSubject.value) return;

    this.isFetchingOverview = true;
    this.loadingSubject.next(true);
    this.errorSubject.next('');

    this.apiService.get<any>('/ownerPortal/dashboard/overview').subscribe({
      next: (response) => {
        // API returns { unitsCount, bookingsCount, totalRevenue, units: [...] }
        let units: OwnerPortalUnit[] = [];

        if (Array.isArray(response?.data)) {
          units = response.data;
        } else if (Array.isArray(response)) {
          units = response;
        } else if (Array.isArray(response?.units)) {
          units = response.units;
        }

        // Prefer the API-provided count fields; fall back to the array length
        const unitsCount = response?.unitsCount ?? response?.totalItems ?? units.length;

        const normalized: OwnerPortalOverviewResponse = {
          unitsCount,
          bookingsCount: response?.bookingsCount ?? 0,
          totalRevenue: response?.totalRevenue ?? 0,
          units,
          pages: response?.pages,
          currentPage: response?.currentPage,
          totalItems: response?.totalItems,
        };

        this.overviewSubject.next(normalized);
        this.loadingSubject.next(false);
        this.isFetchingOverview = false;
      },
      error: () => {
        this.errorSubject.next('ownerPortalDashboard.errors.loadOverview');
        this.loadingSubject.next(false);
        this.isFetchingOverview = false;
      },
    });
  }

  loadProfile(forceRefresh = false): void {
    if (this.isFetchingProfile) return;
    if (!forceRefresh && this.profileSubject.value) return;

    this.isFetchingProfile = true;
    this.apiService.get<OwnerPortalProfileResponse>('/ownerPortal/profile').subscribe({
      next: (response) => {
        this.profileSubject.next(response?.resProfile ?? null);
        this.isFetchingProfile = false;
      },
      error: () => {
        this.errorSubject.next('ownerPortalDashboard.errors.loadProfile');
        this.isFetchingProfile = false;
      },
    });
  }

  loadWallet(forceRefresh = false): void {
    if (this.isFetchingWallet) return;
    if (!forceRefresh && this.walletSubject.value) return;

    this.isFetchingWallet = true;
    this.apiService.get<OwnerPortalWalletResponse>('/ownerPortal/dashboard/wallet').subscribe({
      next: (response) => {
        this.walletSubject.next(response);
        this.isFetchingWallet = false;
      },
      error: () => {
        this.errorSubject.next('ownerPortalDashboard.errors.loadWallet');
        this.isFetchingWallet = false;
      },
    });
  }

  requestPayout(amount: number): Promise<OwnerPortalWalletResponse | null> {
    return new Promise((resolve, reject) => {
      this.apiService
        .post<{ message: string }>('/ownerPortal/dashboard/payout', { amount })
        .subscribe({
          next: () => {
            this.loadWallet(true);
            resolve(this.walletSubject.value);
          },
          error: (error) => reject(error),
        });
    });
  }

  updateProfile(payload: FormData): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apiService.patch('/ownerPortal/profile', payload).subscribe({
        next: () => {
          this.loadProfile(true);
          resolve();
        },
        error: (error) => reject(error),
      });
    });
  }
}
